import { useCallback, useEffect, useMemo, useState } from "react";
import { DISTRIBUTION_PROCESS_STATUS } from "@/constants/distributionStatus";
import {
  ContestGroupType,
  DistributionItemType,
  SelectionStateType,
} from "../types";

const isSelectable = (item: DistributionItemType) =>
  item.statusId === DISTRIBUTION_PROCESS_STATUS.PENDING;

/**
 * Seleção múltipla de distribuições.
 *
 * Só itens pendentes entram na seleção. Quando os dados visíveis mudam
 * (refetch, filtro, troca de aba), uuids que deixaram de ser selecionáveis são
 * descartados automaticamente para não vazarem para uma ação em lote.
 */
export const useDistributionSelection = (contests: ContestGroupType[]) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const selectableItems = useMemo(
    () => contests.flatMap((contest) => contest.items.filter(isSelectable)),
    [contests]
  );

  const selectableByContest = useMemo(() => {
    const map = new Map<string, string[]>();

    contests.forEach((contest) => {
      map.set(contest.id, contest.items.filter(isSelectable).map((item) => item.uuid));
    });

    return map;
  }, [contests]);

  const itemsByUuid = useMemo(() => {
    const map = new Map<string, DistributionItemType>();

    selectableItems.forEach((item) => map.set(item.uuid, item));

    return map;
  }, [selectableItems]);

  // Descarta uuids que não estão mais disponíveis para seleção.
  useEffect(() => {
    setSelected((previous) => {
      if (previous.size === 0) return previous;

      const next = new Set<string>();

      previous.forEach((uuid) => {
        if (itemsByUuid.has(uuid)) next.add(uuid);
      });

      return next.size === previous.size ? previous : next;
    });
  }, [itemsByUuid]);

  const isSelected = useCallback((uuid: string) => selected.has(uuid), [selected]);

  const toggle = useCallback(
    (uuid: string) => {
      setSelected((previous) => {
        const next = new Set(previous);

        if (next.has(uuid)) {
          next.delete(uuid);
        } else {
          next.add(uuid);
        }

        return next;
      });
    },
    []
  );

  const contestState = useCallback(
    (contestId: string): SelectionStateType => {
      const uuids = selectableByContest.get(contestId) ?? [];

      if (uuids.length === 0) return "none";

      const selectedInContest = uuids.filter((uuid) => selected.has(uuid)).length;

      if (selectedInContest === 0) return "none";
      if (selectedInContest === uuids.length) return "all";

      return "partial";
    },
    [selectableByContest, selected]
  );

  const toggleContest = useCallback(
    (contestId: string) => {
      const uuids = selectableByContest.get(contestId) ?? [];

      if (uuids.length === 0) return;

      setSelected((previous) => {
        const next = new Set(previous);
        const allSelected = uuids.every((uuid) => next.has(uuid));

        uuids.forEach((uuid) => {
          if (allSelected) {
            next.delete(uuid);
          } else {
            next.add(uuid);
          }
        });

        return next;
      });
    },
    [selectableByContest]
  );

  const clear = useCallback(() => {
    setSelected((previous) => (previous.size === 0 ? previous : new Set<string>()));
  }, []);

  const selectedItems = useMemo(
    () =>
      Array.from(selected)
        .map((uuid) => itemsByUuid.get(uuid))
        .filter((item): item is DistributionItemType => Boolean(item)),
    [selected, itemsByUuid]
  );

  const selectedUuids = useMemo(
    () => selectedItems.map((item) => item.uuid),
    [selectedItems]
  );

  const selectedQuotas = useMemo(
    () => selectedItems.reduce((total, item) => total + (item.quotas ?? 0), 0),
    [selectedItems]
  );

  return {
    toggle,
    toggleContest,
    clear,
    isSelected,
    contestState,
    selectedItems,
    selectedUuids,
    selectedCount: selectedItems.length,
    selectedQuotas,
  };
};
